import { useAppSelector } from "@app/hooks";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldIconButton } from "@emerald-react/icon-button";
import { EmeraldProgressSpinner } from "@emerald-react/progress-indicator";
import useSurveyState from "@hooks/useSurveyState";
import { useSortableItem } from "@shared/hooks/useSortable";
import { ContactForm } from "@survey-cover/ContactForm";
import { useContactActions } from "@survey-cover/contacts/hooks/useContactActions";
import type {
  ExtendedSurveyContact,
  SurveyContact,
} from "@survey-types/survey.contacts.types";
import { selectEditingContactId } from "@survey/surveySelectors";
import { setContactEditingId } from "@survey/surveySlice";
import { ContactDisplayInfo } from "./ContactDisplayInfo";

export const SortableContactRow = ({
  id,
  contact,
}: {
  readonly id: string;
  readonly contact: ExtendedSurveyContact;
}) => {
  const { attributes, listeners, setNodeRef, style } = useSortableItem(id);
  const { dispatch } = useSurveyState();
  const editingContactId = useAppSelector(selectEditingContactId);
  const { handleRemoveContact, isDeleting } = useContactActions();

  const isEditing = editingContactId === contact.contactId;

  const handleEditClick = () => {
    dispatch(setContactEditingId(contact.contactId));
  };

  const handleRemoveClick = () => {
    if (contact.contactId) {
      handleRemoveContact(contact.contactId);
    }
  };

  return (
    <div
      id="SC-DragHandle"
      ref={setNodeRef}
      style={style}
      className="flex justify-between items-center gap-2"
    >
      <div className="flex flex-row items-center gap-3 w-full">
        {!isEditing && (
          <span {...attributes} {...listeners} className="cursor-grab">
            <EmeraldIcon
              icon="drag_indicator"
              className="text-gray-400 hover:text-gray-600 transition-colors ml-1"
            />
          </span>
        )}
        <div className="flex flex-col gap-1 w-full">
          {isEditing ? (
            <ContactForm
              contact={contact as SurveyContact}
              initialValues={{
                name: contact.name ?? "",
                title: contact.title ?? "",
                email: contact.email ?? "",
                phoneNumber: contact.phoneNumber ?? "",
                licenseNo: contact.licenseNo ?? "",
              }}
            />
          ) : (
            <ContactDisplayInfo contact={contact} />
          )}
        </div>
      </div>
      <div className="flex justify-end gap-x-2">
        {!isEditing && (
          <>
            <EmeraldIconButton
              icon="edit"
              title="Edit"
              type="Icon"
              size="Small"
              onClick={handleEditClick}
              id="SC-EditContact"
            />
            {isDeleting ? (
              <div className="w-8 h-8 flex items-center justify-center">
                <EmeraldProgressSpinner size="small" determinate={false} />
              </div>
            ) : (
              <EmeraldIconButton
                id="SC-DeleteContact"
                icon="clear"
                title="Remove"
                type="Icon"
                size="Small"
                onClick={handleRemoveClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
